import dayjs, { Dayjs } from 'dayjs';
import Cookies from 'js-cookie';
import * as CryptoJS from 'crypto-js';
import { parsePhoneNumberFromString } from "libphonenumber-js/max";


const safeStorage = {
  getItem: (key: string) => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(key);
  },
  setItem: (key: string, value: string) => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, value);
  },
  removeItem: (key: string) => {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(key);
  },
};

export const saveToken = (key: string, token: string) => {
  safeStorage.setItem(key, token);
};

export const getToken = (key: string): string =>
  safeStorage.getItem(key) ?? "";

export const setItem = (key: string, data: any) => {
  safeStorage.setItem(key, JSON.stringify(data));
};

export const getItem = (key: string) => {
  const item = safeStorage.getItem(key);
  return item ? JSON.parse(item) : null;
};

export const isCookiePresent = (cookieName: string) => {
  return Cookies.get(cookieName) !== undefined;
};

export const clearOnLogout = () => {
  safeStorage.removeItem("access_token");
  safeStorage.removeItem("referraltoken");
  safeStorage.removeItem("externalReferralLink");
  safeStorage.removeItem("Permissions");
  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem("permission_703_count");
  }
  Cookies.remove('currentUser', { path: "/" });
};

export const combineClasses = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(" ");

export const onlyNumbersInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (["e", "E", "+", "-", "."].includes(e.key)) {
    e.preventDefault();
  }
};

export const getInitialValues = <T extends Record<string, any>>(config: T) =>
  Object.fromEntries(
    Object.entries(config).map(([key, val]) => [key, val.defaultValue])
  );

export const createEnum = (obj: Record<string, any>) => {
  Object.freeze(obj);
};
export const getTotalTime = (
  checkInTime?: string | null,
  checkOutTime?: string | null
): string => {
  if (!checkInTime || !checkOutTime) return "-";

  const FORMAT = "MM-DD-YYYY hh:mm:ss A";

  const start = dayjs(checkInTime, FORMAT);
  const end = dayjs(checkOutTime, FORMAT);

  if (!start.isValid() || !end.isValid()) return "-";

  const diffMinutes = end.diff(start, "minute");

  if (diffMinutes <= 0) return "-";

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  return `${hours}h ${minutes}m`;
};

export const convertApiDateToISO = (date?: string) => {
  if (!date) return "";

  const parts = date.split("-");

  if (parts.length !== 3) return date;

  const [day, month, year] = parts;

  return `${year}-${month}-${day}`;
};


export const decryptAES = (cipherText: string) => {
  try {
    const key = CryptoJS.enc.Utf8.parse('mysecretkey12345');
    const iv = CryptoJS.enc.Utf8.parse('ThisIsAnIV443322');

    let fixed = decodeURIComponent(cipherText);
    fixed = fixed.replace(/ /g, '+');

    const encrypted = CryptoJS.enc.Base64.parse(fixed);

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encrypted } as any,
      key,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('Decrypt error:', error);
    return '';
  }
}

export const hasAnyPermission = (
  userPermissions: string[] = [],
  requiredPermissions: string[] = []
) => {
  if (!requiredPermissions.length) return true;

  return requiredPermissions.some((perm) =>
    userPermissions.includes(perm)
  );
};

export const formatDate = (date: string | Dayjs) => dayjs(date).format("DD MMM YYYY");

export const hexToRgba = (hex: string, alpha: number) => {
  const normalizedHex = hex.replace("#", "");
  const bigint = Number.parseInt(normalizedHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export function formatNumber(n: number, decimals: number = 1): string {
  if (!isFinite(n)) return "—";

  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";

  const factor = Math.pow(10, decimals);
  const truncate = (value: number) => Math.floor(value * factor) / factor;

  if (abs < 1_000) {
    return `${sign}${Math.trunc(abs)}`;
  }

  if (abs < 1_00_000) {
    return `${sign}${truncate(abs / 1_000)}K`;
  }

  if (abs < 1_00_00_000) {
    return `${sign}${truncate(abs / 1_00_000)}L`;
  }

  return `${sign}${truncate(abs / 1_00_00_000)}Cr`;
}

export const formatToCustomDateTime = (date: any, isStartOfDay: boolean, dateType?: string, isDate?: boolean) => {
  const d = dayjs(date)
    .set("hour", isStartOfDay ? 0 : 23)
    .set("minute", isStartOfDay ? 0 : 59)
    .set("second", isStartOfDay ? 0 : 59)
    .set("millisecond", isStartOfDay ? 0 : 999);

  return dateType === "CloseDate" || isDate
    ? d.format("MM/DD/YYYY")
    : d.format("MM/DD/YYYY hh:mm:ss A");
};


// export const validatePhoneNumber = (
//   digits: string,
//   dialCode: string,
//   label: string
// ): true | string => {
//   if (!digits) return true; 

//   const full = `${dialCode}${digits}`;

//   try {
//     const phoneNumber = parsePhoneNumberFromString(full);

//     if (!phoneNumber || !phoneNumber.isValid()) {
//       return `Enter a valid ${label.toLowerCase()}`;
//     }

//     return true;
//   } catch {
//     return `Enter a valid ${label.toLowerCase()}`;
//   }
// };


export const validatePhoneNumber = (
  digits: string,
  dialCode: string,
  label: string,
  requireMobile: boolean = false
): true | string => {
  if (!digits) return true;

  const full = `${dialCode}${digits}`;

  try {
    const phoneNumber = parsePhoneNumberFromString(full);

    if (!phoneNumber || !phoneNumber.isValid()) {
      return `Enter a valid ${label.toLowerCase()}`;
    }

    if (requireMobile && phoneNumber.getType() !== "MOBILE") {
      return `Enter a valid mobile number`;
    }

    return true;
  } catch {
    return `Enter a valid ${label.toLowerCase()}`;
  }
};